import { RenderSimplePerson } from "@/blocks/persons-and-users/User"
import {
  ContractDepartmentDepartmentDto,
  ContractDepartmentWorkareaDto,
  DepartmentFunction,
  useContractDepartmentGetOrganigramSuspense,
  useContractDepartmentUpdateContractDepartment,
} from "@/orval/gen"
import { showErrorNotification } from "@/utils/form-submission-notifications"
import {
  Container,
  Tree,
  useTree,
  MultiSelect,
  Group,
  TreeNodeData,
  Loader,
  Alert,
  Title,
} from "@mantine/core"
import { IconChevronDown } from "@tabler/icons-react"
import { RenderNode } from "node_modules/@mantine/core/lib/components/Tree/Tree"
import { useCallback, useMemo } from "react"
import {
  DefaultModal,
  DefaultModalProps,
} from "../../../../-components/default-modal"
import { useTranslation } from "react-i18next"

export function OrganisationTreeModal(props: DefaultModalProps) {
  const { t } = useTranslation("app_server")
  return (
    <DefaultModal
      title={<Title order={3}>{t("DepartmentFunctions")}</Title>}
      {...props}
    >
      <OrganisationTree />
    </DefaultModal>
  )
}

const workareaToTree = (
  v: ContractDepartmentWorkareaDto,
  editable: boolean
) => ({
  value: `workarea-${v.workareaId}`,
  label: v.name,
  nodeprops: {
    workareaId: v.workareaId,
  },
  children:
    v.members?.map((c) => ({
      value: `contractdepartment-${c.contractDepartmentId}`,
      label: "",
      nodeProps: {
        contractDepartmentId: c.contractDepartmentId,
        personId: c.personId,
        functions: c.functions,
        editable: editable,
      },
    })) || [],
})

const departmentToTree = (
  v: ContractDepartmentDepartmentDto
): TreeNodeData => ({
  value: `department-${v.departmentId}`,
  label: v.name,
  children:
    v.workareas?.map((x) => workareaToTree(x, v.editable === true)) ?? [],
  nodeProps: {
    departmentId: v.departmentId,
    editable: v.editable,
  },
})

export function OrganisationTree() {
  const { data } = useContractDepartmentGetOrganigramSuspense()
  const { t } = useTranslation("app_server")
  const treeData = useMemo(() => data.map(departmentToTree), data)

  const tree = useTree({
    // initialExpandedState: getTreeExpandedState(treeData, "*"),
  })

  const updateContractDepartment =
    useContractDepartmentUpdateContractDepartment({
      mutation: {
        onError: showErrorNotification,
      },
    })

  const renderNode: RenderNode = useCallback(
    ({ node, level, tree, elementProps, hasChildren, expanded }) => (
      <Group
        {...elementProps}
        pl={level * 16}
        onClick={() => {
          tree.toggleExpanded(node.value)
        }}
      >
        <Group
          h={40}
          onClick={(e) => {
            e.stopPropagation()
            tree.toggleExpanded(node.value)
          }}
          flex="1"
          justify="space-between"
          style={{
            borderBottom: "1px solid var(--mantine-color-dark-4)",
          }}
        >
          <Group>
            <span>
              {node.value.startsWith("contractdepartment-") ? (
                <RenderSimplePerson
                  personId={parseInt(node.nodeProps?.personId)}
                  suspense={false}
                />
              ) : (
                node.label
              )}
            </span>

            {hasChildren && (
              <IconChevronDown
                size={14}
                style={{
                  transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
                }}
              />
            )}
          </Group>

          {node.value.startsWith("contractdepartment-") ? (
            <>
              <MultiSelect
                disabled={node.nodeProps?.editable !== true}
                miw="200px"
                variant="unstyled"
                defaultValue={node.nodeProps?.functions || []}
                clearable={false}
                onChange={(values) => {
                  if (node.nodeProps?.contractDepartmentId) {
                    updateContractDepartment.mutate({
                      data: {
                        contractDepartmentId:
                          node.nodeProps.contractDepartmentId,
                        functions: values as DepartmentFunction[],
                      },
                    })
                  }
                }}
                data={Object.keys(DepartmentFunction).map((v) => ({
                  value: v,
                  label: t(`DepartmentFunction_${v as DepartmentFunction}`),
                }))}
              />
            </>
          ) : null}
        </Group>
      </Group>
    ),
    []
  )

  return (
    <Container size="xl" m="xl" p="md" pos="relative">
      {updateContractDepartment.isPending && (
        <Loader style={{ position: "absolute", top: 4, right: 4 }} size={10} />
      )}
      <Alert mb="md" color="blue" variant="light">
        {t("DepartmentFunctionsModalDescription")}
      </Alert>
      <Tree tree={tree} data={treeData} renderNode={renderNode} />
    </Container>
  )
}
